import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  TextField,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { organizationApi } from '../../../../services/api';
import Modal from '../../../common/Modal';
import { formatDateOnly, toPickerValue } from '../../../../utils/dateOnly';

/**
 * ADR-120 — End (inactivate) / Reactivate Business Unit dialog.
 *
 * `mode="end"`: end date (today by default, future allowed), reason, live preview
 * from the server (blockers, impact, cascade offer), confirmation control.
 * `mode="reactivate"`: reason + confirmation.
 *
 * The server owns every rule: the dialog renders what the preview / rules
 * endpoints return and never decides on its own whether the unit may end.
 */

const CONFIRMATION = Object.freeze({ NONE: 'none', CHECKBOX: 'checkbox', TYPED_NAME: 'typed_name' });
const MODE = Object.freeze({ END: 'end', REACTIVATE: 'reactivate' });

const DATE_FORMAT = 'YYYY-MM-DD';
const todayIso = () => dayjs().format(DATE_FORMAT);

const EndBusinessUnitModal = ({ isOpen, mode = MODE.END, businessUnit, onClose, onDone }) => {
  const [rules, setRules] = useState(null);
  const [endDate, setEndDate] = useState(todayIso());
  const [cascade, setCascade] = useState(false);
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [checked, setChecked] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEnd = mode === MODE.END;

  // Reset every time the dialog opens for a (possibly different) unit
  useEffect(() => {
    if (!isOpen) return;
    setEndDate(todayIso());
    setCascade(false);
    setReason('');
    setConfirmation('');
    setChecked(false);
    setPreview(null);
    setPreviewError(null);
    organizationApi.getBusinessUnitLifecycleRules()
      .then(setRules)
      .catch(() => setRules({ confirmation: CONFIRMATION.TYPED_NAME, allowCascade: false, caller: { canManage: false } }));
  }, [isOpen, businessUnit?.id, mode]);

  // Dry run whenever the date or the cascade choice changes (end mode only)
  useEffect(() => {
    if (!isOpen || !isEnd || !businessUnit?.id || !endDate) {
      return undefined;
    }
    let cancelled = false;
    setIsPreviewing(true);
    organizationApi.getBusinessUnitInactivationPreview(businessUnit.id, { endDate, cascade })
      .then((result) => { if (!cancelled) { setPreview(result); setPreviewError(null); } })
      .catch((err) => { if (!cancelled) { setPreview(null); setPreviewError(err.message || 'Failed to preview'); } })
      .finally(() => { if (!cancelled) setIsPreviewing(false); });
    return () => { cancelled = true; };
  }, [isOpen, isEnd, businessUnit?.id, endDate, cascade]);

  const confirmationMode = (isEnd ? preview?.requirements?.confirmation : rules?.confirmation) || CONFIRMATION.NONE;
  const confirmationText = (isEnd ? preview?.requirements?.confirmationText : null) || businessUnit?.name || '';
  const confirmationSatisfied = confirmationMode === CONFIRMATION.NONE
    || (confirmationMode === CONFIRMATION.CHECKBOX && checked)
    || (confirmationMode === CONFIRMATION.TYPED_NAME && confirmation.trim() === confirmationText);

  const blockers = preview?.blockers || [];
  const forbidden = isEnd ? preview?.forbidden : (rules && !rules.caller?.canManage ? { message: 'You are not allowed to reactivate business units' } : null);
  const cascadeOffer = blockers.find(b => b.code === 'CHILD_OUTLIVES');
  const allowCascade = Boolean(preview?.requirements?.allowCascade ?? rules?.allowCascade);
  // The checkbox stays visible whether the children currently block (unticked) or are
  // part of the cascade (ticked) — the server reports them either way.
  const cascadeChildCount = cascade
    ? (preview?.impact?.cascadeList?.length || 0)
    : (cascadeOffer?.details?.units?.length || 0);

  const canSubmit = !isSubmitting && !forbidden && confirmationSatisfied && (
    isEnd ? (Boolean(preview) && !isPreviewing && blockers.length === 0) : Boolean(rules)
  );

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const confirmationValue = confirmationMode === CONFIRMATION.CHECKBOX ? checked : confirmation;
    try {
      setIsSubmitting(true);
      const result = isEnd
        ? await organizationApi.inactivateBusinessUnit(businessUnit.id, { endDate, reason: reason || undefined, cascade, confirmation: confirmationValue })
        : await organizationApi.reactivateBusinessUnit(businessUnit.id, { reason: reason || undefined, confirmation: confirmationValue });
      onDone(result, { mode, endDate: isEnd ? endDate : null });
    } catch (err) {
      onDone(null, { mode, error: err });
    } finally {
      setIsSubmitting(false);
    }
  };

  const impact = preview?.impact;
  const submitText = isEnd ? `End ${businessUnit?.name || ''} on ${formatDateOnly(endDate)}` : `Reactivate ${businessUnit?.name || ''}`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEnd ? `End ${businessUnit?.name || ''}` : `Reactivate ${businessUnit?.name || ''}`}
      onSubmit={handleSubmit}
      submitText={submitText}
      submitDisabled={!canSubmit}
      cancelText="Cancel"
      width="640px"
    >
      {isEnd ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          The unit keeps its history (reports, manager history, feedback sessions) and stays where it is in the structure.
          The end date is the last day the unit operates; from the moment it is set the unit accepts no new projects, people or child units.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>{businessUnit?.name}</strong> ended on <strong>{formatDateOnly(businessUnit?.endDate)}</strong>
          {businessUnit?.endReason ? <> ({businessUnit.endReason})</> : null}. Reactivating clears the end date and re-opens its default projects.
        </Alert>
      )}

      {isEnd && (
        <DatePicker
          label="End date"
          value={toPickerValue(endDate)}
          onChange={(value) => setEndDate(value && value.isValid() ? value.format(DATE_FORMAT) : '')}
          minDate={preview?.requirements ? dayjs().subtract(preview.requirements.maxBackdateDays, 'day') : undefined}
          maxDate={preview?.requirements ? dayjs().add(preview.requirements.maxFutureDays, 'day') : undefined}
          slotProps={{
            textField: {
              size: 'small',
              helperText: preview?.requirements
                ? `Up to ${preview.requirements.maxBackdateDays} days back, ${preview.requirements.maxFutureDays} days ahead`
                : undefined,
              inputProps: { 'data-testid': 'end-date' },
              sx: { mb: 2, minWidth: 260 }
            }
          }}
        />
      )}

      {isEnd && isPreviewing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">Checking what ending this unit affects…</Typography>
        </Box>
      )}

      {previewError && <Alert severity="error" sx={{ mb: 2 }}>{previewError}</Alert>}
      {forbidden && <Alert severity="error" sx={{ mb: 2 }}>{forbidden.message}</Alert>}

      {isEnd && blockers.length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="blockers">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>This unit cannot be ended on {formatDateOnly(endDate)} yet:</Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
            {blockers.map(b => (
              <Typography key={b.code} component="li" variant="body2">{b.message}</Typography>
            ))}
          </Box>
        </Alert>
      )}

      {isEnd && allowCascade && cascadeChildCount > 0 && (
        <FormControlLabel
          sx={{ mb: 1 }}
          control={<Checkbox checked={cascade} onChange={(e) => setCascade(e.target.checked)} />}
          label={`End the whole subtree (${cascadeChildCount} child unit${cascadeChildCount === 1 ? '' : 's'}) on the same date`}
        />
      )}

      {isEnd && impact && (
        <Box sx={{ mb: 2 }} data-testid="impact">
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            <Chip size="small" label={`${impact.affectedUnits} unit${impact.affectedUnits === 1 ? '' : 's'}`} />
            <Chip size="small" label={`${impact.defaultProjectsToEnd.length} default project${impact.defaultProjectsToEnd.length === 1 ? '' : 's'} will end`} />
            {impact.activeAllocations > 0 && (
              <Chip size="small" color="warning" variant="outlined" label={`${impact.activeAllocations} allocated`} />
            )}
            {impact.pendingFeedbackSessions > 0 && (
              <Chip size="small" variant="outlined" label={`${impact.pendingFeedbackSessions} open feedback session${impact.pendingFeedbackSessions === 1 ? '' : 's'}`} />
            )}
            {impact.owner && <Chip size="small" variant="outlined" label={`Owner: ${impact.owner.fullName}`} />}
          </Box>
          {impact.cascade && impact.cascadeList?.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Also ending: {impact.cascadeList.map(u => u.name).join(', ')}
            </Typography>
          )}
          {impact.warnings?.length > 0 && (
            <Box component="ul" sx={{ pl: 2.5, mt: 0, mb: 0 }}>
              {impact.warnings.map(w => (
                <Typography key={w.code} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {w.message}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      )}

      <TextField
        label="Reason (optional)"
        size="small"
        fullWidth
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        sx={{ mb: 2 }}
      />

      {confirmationMode === CONFIRMATION.TYPED_NAME && (
        <TextField
          label={`Type "${confirmationText}" to confirm`}
          size="small"
          fullWidth
          value={confirmation}
          onChange={(e) => setConfirmation(e.target.value)}
          autoComplete="off"
        />
      )}

      {confirmationMode === CONFIRMATION.CHECKBOX && (
        <FormControlLabel
          control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
          label={isEnd ? 'I understand this ends the unit' : 'I understand this reactivates the unit'}
        />
      )}
    </Modal>
  );
};

EndBusinessUnitModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(['end', 'reactivate']),
  businessUnit: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  /** (result | null, { mode, endDate?, error? }) */
  onDone: PropTypes.func.isRequired,
};

export default EndBusinessUnitModal;
